import { TMachineCategoryPageDataForPDP } from '@internal/page/TMachineCategoryPageData';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { TLocale } from '@internal/utils/machine/i18n/TLocale';
import { getSearchResultsUrl } from '@internal/utils/routing/getSearchResultsUrl';
import { Button } from '@renderer-ui-library/atoms';
import { ClickToCopyWrapper } from '@renderer-ui-library/molecules';
import { Typography } from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { translations } from 'i18n/translations';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { PageContext } from 'page/PageContext';
import { SystemPageKey } from 'page/SystemPageKey';
import { FC, Fragment, memo, useContext, useMemo } from 'react';
import { MachineContext } from 'utils/machine/MachineContext';
import { useMachineTypeName } from 'utils/machine/useMachineTypeName';
import { PageTypeKey } from 'utils/page/PageTypeKey';
import styles from './breadCrumb.module.scss';

type Entry =
    | {
          type: 'link';
          label: string;
          url: string;
      }
    | {
          type: 'placeholder';
      };

interface Props {
    locale: TLocale;
    machineCategoryPages: {
        forMachineType: TMachineCategoryPageDataForPDP | null;
        forMachineTypeAndBrand: TMachineCategoryPageDataForPDP | null;
        forMachineTypeBrandAndModel: TMachineCategoryPageDataForPDP | null;
    };
}

export const BreadCrumb: FC<Props> = memo(props => {
    const { machine } = useContext(MachineContext);
    const { pageData } = useContext(PageContext);
    const { getMachineTypeName } = useMachineTypeName();
    const { t } = useTranslation(localeNamespace.common);
    const { isMobile } = useMediaQuery();

    const entries = useMemo(() => {
        const entries: Entry[] = isMobile
            ? []
            : [
                  {
                      type: 'link',
                      url: '/',
                      label: t(translations.breadcrumbHomeLabel),
                  },
              ];

        if (
            pageData?.type === PageTypeKey.SystemPage &&
            pageData.key === SystemPageKey.PDP &&
            machine
        ) {
            const machineTypeName = getMachineTypeName(
                machine.machineType,
                'plural',
            );
            entries.push(
                {
                    type: 'link',
                    url:
                        props.machineCategoryPages.forMachineType
                            ?.localizations[props.locale] ??
                        getSearchResultsUrl(props.locale, {
                            machineTypeName,
                        }),
                    label: machineTypeName,
                },
                machine.brand
                    ? {
                          type: 'link',
                          url:
                              props.machineCategoryPages.forMachineTypeAndBrand
                                  ?.localizations[props.locale] ??
                              getSearchResultsUrl(props.locale, {
                                  machineTypeName,
                                  brands: [machine.brand],
                              }),
                          label: machine.brand,
                      }
                    : { type: 'placeholder' },
                machine.model
                    ? {
                          type: 'link',
                          url:
                              props.machineCategoryPages
                                  .forMachineTypeBrandAndModel?.localizations[
                                  props.locale
                              ] ??
                              getSearchResultsUrl(props.locale, {
                                  machineTypeName,
                                  brands: machine.brand
                                      ? [machine.brand]
                                      : undefined,
                                  models: machine.model
                                      ? [
                                            {
                                                name:
                                                    machine.data.MAPPED_MODEL ||
                                                    machine.data.BASE_MODEL ||
                                                    machine.model,
                                                type:
                                                    (!!machine.data
                                                        .MAPPED_MODEL &&
                                                        'mapped') ||
                                                    (!!machine.data
                                                        .BASE_MODEL &&
                                                        'base') ||
                                                    'fuzzy',
                                            },
                                        ]
                                      : undefined,
                              }),
                          label: machine.model,
                      }
                    : { type: 'placeholder' },
            );
        }

        return entries;
    }, [
        getMachineTypeName,
        isMobile,
        machine,
        pageData,
        props.machineCategoryPages,
        props.locale,
        t,
    ]);

    return (
        <div className={styles.wrapper}>
            {entries.map((entry, index) => {
                return (
                    <Fragment
                        key={
                            entry.type === 'link'
                                ? entry.url
                                : `${entry.type}-${index}`
                        }
                    >
                        {entry.type === 'link' && (
                            <Link href={entry.url} passHref prefetch={false}>
                                <Button variant="text" size="small" noMinWidth>
                                    <Typography
                                        variant={isMobile ? 'caption' : 'body1'}
                                        color="text.secondary"
                                    >
                                        {entry.label}
                                    </Typography>
                                </Button>
                            </Link>
                        )}
                        {entry.type === 'placeholder' && (
                            <Typography
                                variant={isMobile ? 'caption' : 'body1'}
                                color="text.secondary"
                            >
                                -
                            </Typography>
                        )}

                        <Typography
                            variant={isMobile ? 'caption' : 'body1'}
                            color="text.secondary"
                            className={styles.divider}
                        >
                            /
                        </Typography>
                    </Fragment>
                );
            })}
            {machine && (
                <ClickToCopyWrapper text={machine.machineGroupId}>
                    <Typography
                        variant={isMobile ? 'caption' : 'body1'}
                        color="text.secondary"
                        className={styles.machineGroupId}
                    >{`${machine.machineGroupId}`}</Typography>
                </ClickToCopyWrapper>
            )}
        </div>
    );
});

BreadCrumb.displayName = 'BreadCrumb';
